import { useState, useEffect } from "react";
import moment from "moment";
import { displayDate, displayTime } from "@/lib/timeUtils";
import { 
  Calendar, 
  Clock, 
  Video, 
  User,
  X,
  Phone,
  Mail,
  FileText,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

const statusConfig = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700 hover:bg-blue-200" },
  waiting: { label: "Waiting", color: "bg-amber-100 text-amber-700 hover:bg-amber-200" },
  in_progress: { label: "In Progress", color: "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" },
  completed: { label: "Completed", color: "bg-muted text-muted-foreground hover:bg-muted" },
  cancelled: { label: "Cancelled", color: "bg-destructive/10 text-destructive hover:bg-destructive/20" },
};

export interface AppointmentData {
  id: string;
  member_id: string;
  member_name?: string;
  status: string;
  visit_type?: string;
  visit_reason?: string;
  notes?: string;
  scheduled_date?: string;
  scheduled_time?: string;
  duration_minutes?: number;
}

interface ScheduleItemDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: AppointmentData | null;
  onStartSession?: (sessionId: string) => void;
  isStartingSession?: boolean;
  onRejoinSession?: (sessionId: string) => void;
}

export function ScheduleItemDetailsDialog({
  open,
  onOpenChange,
  appointment,
  onStartSession,
  isStartingSession,
  onRejoinSession
}: ScheduleItemDetailsDialogProps) {
  // Try to fetch full member details like email and phone
  const { data: memberProfile } = useQuery({
    queryKey: ["member-contact-info", appointment?.member_id],
    queryFn: async () => {
      if (!appointment?.member_id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', appointment.member_id)
        .single();
        
      if (error) {
        console.error("Error fetching member info:", error);
        return null;
      }
      return data;
    },
    enabled: !!appointment?.member_id && open,
  });

  if (!appointment) return null;

  const status = appointment.status as keyof typeof statusConfig;
  const statusBadge = statusConfig[status] || statusConfig.scheduled;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
        <div className="bg-primary/5 p-6 pb-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-2 border-background shadow-sm">
                <AvatarFallback className="bg-primary/20 text-primary text-xl font-medium">
                  {appointment.member_name?.split(" ").map((n: string) => n[0]).join("") || "U"}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{appointment.member_name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="capitalize font-medium">
                    {appointment.visit_type?.replace("_", " ") || "Telehealth"}
                  </Badge>
                  <Badge className={`${statusBadge.color} border-0`}>
                    {statusBadge.label}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="p-6 space-y-6">
            {/* Appointment Details */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Session Details</h3>
              <div className="grid grid-cols-2 gap-4 bg-muted/30 p-4 rounded-lg border">
                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Date</p>
                    <p className="text-sm text-muted-foreground">
                      {appointment.scheduled_date ? displayDate(appointment.scheduled_date) : "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium">Time</p>
                    <p className="text-sm text-muted-foreground">
                      {appointment.scheduled_date && appointment.scheduled_time
                        ? displayTime(appointment.scheduled_time, appointment.scheduled_date)
                        : appointment.scheduled_time?.slice(0, 5) || "N/A"} ({appointment.duration_minutes || 30} min)
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Visit Reason and Notes */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Clinical Context</h3>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Reason for Visit</span>
                  </div>
                  <p className="text-sm bg-muted/30 p-3 rounded-md border text-foreground">
                    {appointment.visit_reason || "No specific reason provided."}
                  </p>
                </div>

                {appointment.notes && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Patient Notes</span>
                    </div>
                    <p className="text-sm bg-muted/30 p-3 rounded-md border text-foreground whitespace-pre-wrap">
                      {appointment.notes}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Patient Contact Info */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Contact Information</h3>
              <div className="space-y-2 bg-muted/30 p-4 rounded-lg border">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-foreground">
                    {memberProfile?.email || "No email available"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        {/* Actions */}
        <div className="p-4 border-t bg-muted/10 flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          
          {(status === "scheduled" || status === "waiting") && onStartSession && (
            <Button 
              onClick={() => onStartSession(appointment.id)}
              disabled={isStartingSession}
              className={status === "waiting" ? "bg-amber-600 hover:bg-amber-700 text-white" : ""}
            >
              <Video className="h-4 w-4 mr-2" />
              {status === "waiting" ? "Join Waiting Patient" : "Start Session"}
            </Button>
          )}

          {status === "in_progress" && onRejoinSession && (
            <Button 
              variant="success"
              onClick={() => onRejoinSession(appointment.id)}
            >
              <Video className="h-4 w-4 mr-2" />
              Rejoin Session
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
